import {
  ActionMenu,
  Button,
  ClassificationBadge,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Mono,
  ResourceTable,
  SectionCard,
  Select,
  StatusBadge,
  TextInput,
  environmentKindLabel,
  type Column,
  type EnvironmentClassification,
  type EnvironmentKind,
} from '@verydia/ui';
import type { EnvironmentResource } from '@verydia/contracts';
import { VerydiaApiError } from '@verydia/sdk';
import { Archive, Layers, Pencil, Plus, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { describeFailure, isStarting } from '../lib/api.ts';
import {
  useArchiveEnvironment,
  useCreateEnvironment,
  useEnvironments,
  useUpdateEnvironment,
} from '../lib/identity-queries.ts';
import { CopyableId } from './copyable-id.tsx';

const KINDS: readonly EnvironmentKind[] = [
  'DEVELOPMENT',
  'TEST',
  'STAGING',
  'PILOT',
  'PRODUCTION',
  'CUSTOM',
];

const CLASSIFICATIONS: readonly EnvironmentClassification[] = ['NON_PRODUCTION', 'PRODUCTION'];

const CLASSIFICATION_LABEL: Record<EnvironmentClassification, string> = {
  NON_PRODUCTION: 'Non-production',
  PRODUCTION: 'Production',
};

/**
 * What a kind usually implies — offered, never applied.
 *
 * Mirrors `suggestedClassification` in the domain, deliberately at the form
 * layer only. If a service called it, the "suggestion" would be applied after
 * the operator had already decided, which is inference with an extra step
 * (ADR-018).
 */
function suggestedClassification(kind: EnvironmentKind): EnvironmentClassification {
  return kind === 'PRODUCTION' ? 'PRODUCTION' : 'NON_PRODUCTION';
}

function slugFromName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

function isConflict(error: unknown): boolean {
  return error instanceof VerydiaApiError && error.code === 'resource_conflict';
}

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/* ------------------------------ shared fields ------------------------------ */

/**
 * Classification, always visible and always editable.
 *
 * Never hidden behind a default. This is the value future licence accounting
 * reads, and a field nobody saw is a decision nobody made.
 */
function ClassificationField({
  value,
  onChange,
  suggestedFor,
}: {
  value: EnvironmentClassification;
  onChange: (next: EnvironmentClassification) => void;
  suggestedFor?: EnvironmentKind | undefined;
}) {
  const suggestion = suggestedFor === undefined ? undefined : suggestedClassification(suggestedFor);
  const overridden = suggestion !== undefined && suggestion !== value;

  return (
    <Field
      label="Classification"
      required
      description={
        overridden
          ? 'Deliberately different from what this kind usually implies. That is allowed — the kind describes your workflow, this decides how the environment is counted.'
          : 'Determines production versus non-production semantics for future licensing and usage accounting.'
      }
    >
      {(props) => (
        <Select
          {...props}
          value={value}
          onChange={(event) => onChange(event.target.value as EnvironmentClassification)}
        >
          {CLASSIFICATIONS.map((option) => (
            <option key={option} value={option}>
              {CLASSIFICATION_LABEL[option]}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

function KindField({
  value,
  onChange,
}: {
  value: EnvironmentKind;
  onChange: (next: EnvironmentKind) => void;
}) {
  return (
    <Field label="Kind" required description="What you call this stage of your process.">
      {(props) => (
        <Select
          {...props}
          value={value}
          onChange={(event) => onChange(event.target.value as EnvironmentKind)}
        >
          {KINDS.map((option) => (
            <option key={option} value={option}>
              {environmentKindLabel(option)}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

/* --------------------------------- create ---------------------------------- */

function CreateEnvironmentDialog({
  organizationId,
  productId,
  productName,
  open,
  onOpenChange,
}: {
  organizationId: string;
  productId: string;
  productName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [kind, setKind] = useState<EnvironmentKind>('DEVELOPMENT');
  const [classification, setClassification] = useState<EnvironmentClassification>('NON_PRODUCTION');
  const [classificationEdited, setClassificationEdited] = useState(false);

  const create = useCreateEnvironment(organizationId, productId);
  const failure = create.isError ? describeFailure(create.error) : null;
  const slugConflict = failure !== null && /already uses/i.test(failure.message);

  const effectiveSlug = slugEdited ? slug.trim() : slugFromName(name);
  // Required means required. The blank-slug defect shipped once because the
  // field said required and the form accepted an empty effective value.
  const canSubmit = name.trim() !== '' && effectiveSlug !== '' && !create.isPending;

  const close = () => {
    create.reset();
    setName('');
    setSlug('');
    setSlugEdited(false);
    setKind('DEVELOPMENT');
    setClassification('NON_PRODUCTION');
    setClassificationEdited(false);
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title="Create an environment"
      description={`Where ${productName} is developed, tested or operated.`}
      footer={
        <>
          <Button onClick={close} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={<Plus />}
            disabled={!canSubmit}
            onClick={() =>
              create.mutate(
                {
                  name: name.trim(),
                  slug: effectiveSlug,
                  kind,
                  classification,
                },
                { onSuccess: close },
              )
            }
          >
            {create.isPending ? 'Creating…' : 'Create environment'}
          </Button>
        </>
      }
    >
      <form className="flex flex-col gap-4" onSubmit={(event) => event.preventDefault()}>
        <Field label="Environment name" required description="What your team calls it.">
          {(props) => (
            <TextInput
              {...props}
              placeholder="Development"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>

        <Field
          label="Slug"
          required
          description="Used in API paths and integrations, and unique within this product. Permanent once set."
          error={slugConflict ? failure.message : undefined}
        >
          {(props) => (
            <TextInput
              {...props}
              placeholder="development"
              value={slugEdited ? slug : slugFromName(name)}
              onChange={(event) => {
                setSlugEdited(true);
                setSlug(event.target.value);
              }}
            />
          )}
        </Field>

        <KindField
          value={kind}
          onChange={(next) => {
            setKind(next);
            // The suggestion follows the kind until the operator touches
            // classification themselves. After that it stays where they put it:
            // silently rewriting an explicit choice is the inference this field
            // exists to avoid.
            if (!classificationEdited) setClassification(suggestedClassification(next));
          }}
        />

        <ClassificationField
          value={classification}
          suggestedFor={kind}
          onChange={(next) => {
            setClassificationEdited(true);
            setClassification(next);
          }}
        />

        {failure !== null && !slugConflict ? (
          <p role="alert" className="text-[12px] text-[var(--vc-danger-text)]">
            {failure.message}
            {failure.requestId !== null ? (
              <>
                {' '}
                Request <Mono>{failure.requestId}</Mono>
              </>
            ) : null}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

/* ---------------------------------- edit ----------------------------------- */

function EditEnvironmentDialog({
  organizationId,
  productId,
  environment,
  open,
  onOpenChange,
  onReload,
}: {
  organizationId: string;
  productId: string;
  environment: EnvironmentResource;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReload: () => void;
}) {
  const [name, setName] = useState(environment.name);
  const [kind, setKind] = useState<EnvironmentKind>(environment.kind);
  const [classification, setClassification] = useState<EnvironmentClassification>(
    environment.classification,
  );

  const update = useUpdateEnvironment(organizationId, productId, environment.id);
  const failure = update.isError ? describeFailure(update.error) : null;
  const conflict = isConflict(update.error);

  const close = () => {
    update.reset();
    setName(environment.name);
    setKind(environment.kind);
    setClassification(environment.classification);
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title={conflict ? 'This environment changed since you opened it' : `Edit ${environment.name}`}
      description={
        conflict
          ? 'Someone else saved a change while this page was open. Your edit was not applied, and theirs was kept.'
          : 'Changes are recorded in the audit log.'
      }
      footer={
        conflict ? (
          <>
            <Button onClick={close}>Discard my change</Button>
            <Button
              variant="primary"
              icon={<RotateCcw />}
              onClick={() => {
                update.reset();
                onReload();
              }}
            >
              Reload latest version
            </Button>
          </>
        ) : (
          <>
            <Button onClick={close} disabled={update.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={update.isPending || name.trim() === ''}
              onClick={() =>
                update.mutate(
                  {
                    expected_version: environment.version,
                    name: name.trim(),
                    kind,
                    classification,
                  },
                  { onSuccess: close },
                )
              }
            >
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </>
        )
      }
    >
      {conflict ? (
        <p className="text-[13px] text-[var(--vc-text-muted)]">
          Reload to see the current version, then reapply your change if it is still needed.
        </p>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={(event) => event.preventDefault()}>
          <Field label="Environment name" required>
            {(props) => (
              <TextInput
                {...props}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>

          {/*
            Read-only rather than absent, and read-only rather than disabled.
            Disabled reads as "you are not allowed", which is a different and
            wrong explanation — nobody is, including the owner. The API refuses
            a slug change outright, so this is not a form-only rule.
          */}
          <Field
            label="Slug"
            description="Permanent. It appears in API paths, credentials and runtime configuration, which cannot follow a rename."
          >
            {(props) => (
              <TextInput
                {...props}
                readOnly
                value={environment.slug}
                className="bg-[var(--vc-surface-sunken)] text-[var(--vc-text-muted)]"
              />
            )}
          </Field>

          <KindField value={kind} onChange={setKind} />

          <ClassificationField value={classification} onChange={setClassification} />

          {failure !== null ? (
            <p role="alert" className="text-[12px] text-[var(--vc-danger-text)]">
              {failure.message}
            </p>
          ) : null}
        </form>
      )}
    </Dialog>
  );
}

/* --------------------------------- archive --------------------------------- */

function ArchiveEnvironmentDialog({
  organizationId,
  productId,
  environment,
  open,
  onOpenChange,
}: {
  organizationId: string;
  productId: string;
  environment: EnvironmentResource;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const archive = useArchiveEnvironment(organizationId, productId, environment.id);
  const failure = archive.isError ? describeFailure(archive.error) : null;

  const close = () => {
    archive.reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title={`Archive ${environment.name}?`}
      description="Archived environments are removed from normal active selection. Historical records remain preserved."
      footer={
        <>
          <Button onClick={close} disabled={archive.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            icon={<Archive />}
            disabled={archive.isPending}
            onClick={() =>
              archive.mutate({ expected_version: environment.version }, { onSuccess: close })
            }
          >
            {archive.isPending ? 'Archiving…' : 'Archive environment'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-[13px] text-[var(--vc-text-muted)]">
        <p>
          This environment keeps its identity and its history. It stops appearing where an active
          environment is expected.
        </p>
        {failure !== null ? (
          <p role="alert" className="text-[var(--vc-danger-text)]">
            {failure.message}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

/* ---------------------------------- panel ---------------------------------- */

/**
 * One environments surface, used from two places.
 *
 * The product's own Environments tab and Developers → Environments render this
 * same component against the same query. A second list — however small — is a
 * second thing to keep true, and the two would disagree the first time one of
 * them was changed.
 */
export function EnvironmentsPanel({
  organizationId,
  productId,
  productName,
  productIsActive,
}: {
  organizationId: string;
  productId: string;
  productName: string;
  productIsActive: boolean;
}) {
  const environments = useEnvironments(organizationId, productId);
  const failure = environments.isError ? describeFailure(environments.error) : null;
  const starting =
    environments.isFetching && isStarting(environments.failureReason ?? environments.error);
  const rows: readonly EnvironmentResource[] = environments.data?.data.data ?? [];

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<EnvironmentResource | null>(null);
  const [archiving, setArchiving] = useState<EnvironmentResource | null>(null);

  const columns: ReadonlyArray<Column<EnvironmentResource>> = [
    {
      key: 'name',
      header: 'Environment',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-medium text-[var(--vc-text)]">{row.name}</span>
          <Mono className="text-[11.5px] text-[var(--vc-text-subtle)]">{row.slug}</Mono>
        </div>
      ),
    },
    {
      key: 'kind',
      header: 'Kind',
      render: (row) => (
        <span className="text-[var(--vc-text-muted)]">{environmentKindLabel(row.kind)}</span>
      ),
    },
    {
      key: 'classification',
      header: 'Classification',
      render: (row) => <ClassificationBadge classification={row.classification} />,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <StatusBadge tone={row.status === 'ACTIVE' ? 'success' : 'neutral'}>
          {row.status === 'ACTIVE' ? 'Active' : 'Archived'}
        </StatusBadge>
      ),
    },
    {
      key: 'id',
      header: 'Environment ID',
      render: (row) => <CopyableId value={row.id} label={`${row.name} environment id`} />,
    },
    {
      key: 'updated',
      header: 'Updated',
      align: 'right',
      render: (row) => (
        <span className="tabular-nums text-[var(--vc-text-muted)]">
          {formatTimestamp(row.updated_at)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      // Always reachable, whatever the width. See Column.pin.
      pin: 'right',
      render: (row) =>
        row.status === 'ACTIVE' ? (
          <ActionMenu
            label={`Actions for ${row.name}`}
            items={[
              {
                key: 'edit',
                label: 'Edit environment',
                icon: <Pencil />,
                onSelect: () => setEditing(row),
              },
              {
                key: 'archive',
                label: 'Archive environment',
                icon: <Archive />,
                destructive: true,
                onSelect: () => setArchiving(row),
              },
            ]}
          />
        ) : null,
    },
  ];

  return (
    <>
      <SectionCard
        title="Environments"
        description={`Where ${productName} is developed, tested or operated.`}
        actions={
          productIsActive ? (
            <Button variant="primary" icon={<Plus />} onClick={() => setCreateOpen(true)}>
              Create environment
            </Button>
          ) : null
        }
        flush
      >
        {/*
          Loading, starting, error and empty are four different answers. Showing
          "No environments yet" while the request is in flight is a lie the
          operator has no way to detect.
        */}
        {starting ? (
          <LoadingState label="Verydia is starting up" />
        ) : environments.isPending ? (
          <LoadingState label="Loading environments" />
        ) : failure !== null ? (
          <ErrorState
            title="Could not load environments"
            description={failure.message}
            requestId={failure.requestId}
            onRetry={() => void environments.refetch()}
          />
        ) : (
          <ResourceTable
            caption={`Environments of ${productName}`}
            columns={columns}
            rows={[...rows]}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                icon={<Layers />}
                title="No environments yet"
                description={`Define the environments where ${productName} is developed, tested, or operated.`}
                action={
                  productIsActive ? (
                    <Button variant="primary" icon={<Plus />} onClick={() => setCreateOpen(true)}>
                      Create environment
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        )}
      </SectionCard>

      <CreateEnvironmentDialog
        organizationId={organizationId}
        productId={productId}
        productName={productName}
        open={createOpen}
        onOpenChange={setCreateOpen}
      />

      {editing !== null ? (
        <EditEnvironmentDialog
          key={`edit-${editing.id}-${editing.version}`}
          organizationId={organizationId}
          productId={productId}
          environment={editing}
          open
          onOpenChange={(next) => (next ? undefined : setEditing(null))}
          onReload={() => void environments.refetch()}
        />
      ) : null}

      {archiving !== null ? (
        <ArchiveEnvironmentDialog
          key={`archive-${archiving.id}-${archiving.version}`}
          organizationId={organizationId}
          productId={productId}
          environment={archiving}
          open
          onOpenChange={(next) => (next ? undefined : setArchiving(null))}
        />
      ) : null}
    </>
  );
}
